/**
 * S7 — Home health. The platform-agents substrate's own state.
 *
 * Renders the ⚖ DISABLED-vs-BROKEN split as two visibly different situations
 * (a deployment choice vs an incident), and keeps a failed READ as a third
 * thing rather than letting it land on either. The rule lives in
 * `lib/specialist-home-state.ts` so it is testable without a DOM; this file is
 * presentation over that verdict and holds no health logic of its own.
 *
 * THE JOBS PANEL, AND WHY IT DID NOT EXIST FOR A DAY. The handoff's S7 row named
 * drain/gc job status; slice 3 shipped `/state` alone, so this panel was left
 * ABSENT rather than empty — an empty "Jobs" section reads as "no jobs are
 * running", a claim the page could not support. That call turned out to be
 * righter than the reasoning behind it: lexserver's answer was that the jobs
 * recorded NOTHING, so the only observable was that timers had been created —
 * and a job throwing on every tick creates timers exactly as happily as a
 * healthy one. A panel built then could not have reported a fault IN PRINCIPLE.
 * `GET …/jobs` (lexserver `865349001`) now records each tick, and the panel
 * below reads it through `lib/specialist-jobs.ts`, where `null` (never ran) is
 * kept strictly apart from `ok`.
 */

import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  HelpCircle,
  PowerOff,
  RefreshCw,
  Timer,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { probe, type Probe } from "@/lib/probe";
import { homeSeverity, selectHomeView, type HomeView } from "@/lib/specialist-home-state";
import { describeAge, jobStaleness, outcomeGap, selectJobView } from "@/lib/specialist-jobs";
import {
  getSpecialistHomeState,
  getSpecialistJobs,
  type SpecialistHomeState,
  type SpecialistJobs,
} from "@/lib/specialists-api";

const TONE: Record<ReturnType<typeof homeSeverity>, string> = {
  ok: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-300",
  notice: "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300",
  incident: "border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300",
  unread: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300",
};

function Banner({ view }: { view: HomeView }) {
  const tone = TONE[homeSeverity(view)];
  const base = `flex items-start gap-2 rounded-md border p-3 text-sm ${tone}`;

  switch (view.kind) {
    case "ready":
      return (
        <div className={base} data-testid="specialist-health-ready">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">Home is ready</div>
            <div className="mt-0.5 text-xs opacity-90">
              Provisioned and healthy — {view.roster.length} specialist
              {view.roster.length === 1 ? "" : "s"} own a workspace.
            </div>
          </div>
        </div>
      );
    case "disabled":
      return (
        <div className={base} data-testid="specialist-health-disabled">
          <PowerOff className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">Home is disabled</div>
            <div className="mt-0.5 text-xs opacity-90">
              No platform-agents root is configured, so every specialist surface refuses. This is a
              deployment choice, <span className="font-medium">not a fault</span> — nothing to
              investigate unless you expected it to be on.
            </div>
          </div>
        </div>
      );
    case "broken":
      return (
        <div className={base} data-testid="specialist-health-broken">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">Home is BROKEN — this is a fault</div>
            <div className="mt-0.5 text-xs opacity-90">
              A root is configured but could not be made good. Specialist surfaces refuse exactly as
              they would if disabled, but this one needs an operator.
            </div>
            <div className="mt-1.5 font-mono text-xs" data-testid="specialist-health-broken-reason">
              {view.reason}
            </div>
          </div>
        </div>
      );
    case "unrecognized":
      return (
        <div className={base} data-testid="specialist-health-unrecognized">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">Unrecognised home state</div>
            <div className="mt-0.5 text-xs opacity-90">
              lexserver reported <span className="font-mono">{view.state}</span>, which this console
              has no rendering for. Treated as needing attention rather than assumed healthy — this
              page is older than the server it is reading.
            </div>
          </div>
        </div>
      );
    case "unread":
      return (
        <div className={base} data-testid="specialist-health-unread">
          <HelpCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">Could not read the home state</div>
            <div className="mt-0.5 text-xs opacity-90">
              This is <span className="font-medium">not</span> a report that the home is disabled or
              broken — nothing below is a reading of the substrate.
            </div>
            <div className="mt-1.5 font-mono text-xs" data-testid="specialist-health-unread-reason">
              {view.reason}
            </div>
          </div>
        </div>
      );
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-slate-100 py-2 last:border-0 dark:border-slate-800 sm:flex-row sm:items-baseline sm:gap-3">
      <span className="w-40 shrink-0 text-xs font-medium text-slate-500 dark:text-slate-400">
        {label}
      </span>
      <span className="min-w-0 break-all font-mono text-xs">{children}</span>
    </div>
  );
}


/** Which interval governs a job. `drain` is the fast one; everything else daily. */
function intervalFor(name: string, intervals: SpecialistJobs["intervals"]): number | undefined {
  return name === "drain" ? intervals.drainMs : intervals.dailyMs;
}

function JobsPanel({ result }: { result: Probe<SpecialistJobs> | null }) {
  if (result === null) return null;

  if (!result.ok) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Timer className="h-4 w-4" />
            Maintenance jobs
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div
            className="rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
            data-testid="specialist-jobs-unread"
          >
            Could not read the maintenance jobs — {result.error}. This is{" "}
            <span className="font-medium">not</span> a report that they are idle or healthy.
          </div>
        </CardContent>
      </Card>
    );
  }

  const { scheduled, intervals, jobs } = result.value;
  const names = Object.keys(jobs ?? {});
  const now = Date.now();
  const startedAt = result.value.since;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-medium text-muted-foreground">
          <Timer className="h-4 w-4" />
          Maintenance jobs
          {/* A fact about TIMERS. Deliberately not styled as health: armed while
              every tick fails is precisely what this panel exists to expose, so
              `scheduled` never colours a job row below. */}
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${
              scheduled
                ? "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
            }`}
            title={
              scheduled
                ? "The timers are armed. This says nothing about whether the ticks succeed — read each job below."
                : "The timers are NOT armed. Nothing below will advance."
            }
            data-testid="specialist-jobs-scheduled"
          >
            {scheduled ? "timers armed" : "TIMERS NOT ARMED"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {names.length === 0 && (
          <p className="py-3 text-xs text-slate-400">The server reported no jobs.</p>
        )}
        {names.map((name) => {
          const view = selectJobView(jobs[name]);
          const interval = intervalFor(name, intervals ?? {});
          const stale = jobStaleness(view, interval, now);
          // Turns a MISSING outcome from merely-honest into diagnostic: a job
          // with nothing recorded on a server up for hours is a real signal,
          // and one that durable persistence would not provide.
          const gap = outcomeGap(view, interval, startedAt, now);
          return (
            <div
              key={name}
              className="flex flex-wrap items-baseline gap-2 border-b border-slate-100 py-2 text-xs last:border-0 dark:border-slate-800"
              data-testid={`specialist-job-${name}`}
            >
              <span className="w-20 shrink-0 font-mono font-medium">{name}</span>
              {view.kind === "no-outcome" && (
                <span
                  className={`inline-flex items-center gap-1 ${
                    gap === "overdue"
                      ? "text-amber-600 dark:text-amber-500"
                      : "text-slate-500 dark:text-slate-400"
                  }`}
                >
                  <CircleDashed className="h-3 w-3" />
                  {/* NOT "never ran". The outcomes live in an in-process Map, so
                      a restart wipes them — claiming "never ran" false-alarms on
                      three healthy jobs after every routine deploy, and a panel
                      that cries wolf on a deploy is one an operator stops
                      reading. This is the weaker claim, which is the true one. */}
                  no outcome recorded in this process
                  {gap === "expected" && startedAt && (
                    <span className="text-slate-400">
                      — this process started {describeAge(now - Date.parse(startedAt))}; not due yet
                    </span>
                  )}
                  {gap === "overdue" && (
                    <span className="font-medium">— and it was due; this one is worth looking at</span>
                  )}
                  {(gap === null || gap === "indeterminate") && (
                    <span className="text-slate-400">— cannot say whether it is due</span>
                  )}
                </span>
              )}
              {view.kind === "ok" && (
                <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-3 w-3" />
                  ok
                </span>
              )}
              {view.kind === "failed" && (
                <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                  <AlertTriangle className="h-3 w-3" />
                  failed
                </span>
              )}
              {view.kind === "unrecognized" && (
                <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-500">
                  <HelpCircle className="h-3 w-3" />
                  reported <span className="font-mono">{view.outcome}</span> — unrecognised
                </span>
              )}
              {view.kind !== "no-outcome" && (
                <span
                  className={
                    stale?.overdue
                      ? "font-medium text-amber-600 dark:text-amber-500"
                      : "text-slate-400"
                  }
                  title={view.lastRunAt}
                >
                  {describeAge(stale ? stale.ageMs : now - Date.parse(view.lastRunAt))}
                  {/* A green "ok" from nine hours ago on a 60-second timer is a
                      stuck job wearing a healthy badge. The cadence is what
                      makes the age mean anything. */}
                  {stale?.overdue && " — OVERDUE for its cadence"}
                </span>
              )}
              {view.kind === "ok" && view.durationMs !== undefined && (
                <span className="text-slate-400 tabular-nums">{view.durationMs}ms</span>
              )}
              {view.kind === "failed" && (
                <span className="w-full font-mono text-red-600 dark:text-red-400">{view.error}</span>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export function SpecialistHealth() {
  const [result, setResult] = useState<Probe<{ home: SpecialistHomeState }> | null>(null);
  const [jobs, setJobs] = useState<Probe<SpecialistJobs> | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // Settled probes: a rejection is a RESULT here, not an exception that blanks
    // the panel into something indistinguishable from "disabled". The two settle
    // INDEPENDENTLY — an unreadable jobs endpoint must not cost us the home
    // state, which is the more important of the two.
    const [home, j] = await Promise.all([probe(getSpecialistHomeState()), probe(getSpecialistJobs())]);
    setResult(home);
    setJobs(j);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const view = selectHomeView(result);

  return (
    <div className="flex flex-col gap-3" data-testid="specialist-health">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
        {loading && result === null && (
          <span className="text-xs text-slate-500 dark:text-slate-400">Reading…</span>
        )}
      </div>

      {/* Nothing renders a verdict before the first read settles — a banner
          shown during load would be a claim about state we have not read. */}
      {(result !== null || !loading) && <Banner view={view} />}

      {view.kind === "ready" && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Substrate</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Field label="Root">{view.root}</Field>
            <Field label="Shared path">{view.sharedPath}</Field>
            <Field label="Roster">
              {view.roster.length === 0 ? (
                <span className="text-slate-400">
                  none — the home is ready with no specialist workspaces
                </span>
              ) : (
                view.roster.join(", ")
              )}
            </Field>
            <Field label="Created this boot">
              {view.created.length === 0 ? (
                <span className="text-slate-500">
                  none — the tree was already converged
                </span>
              ) : (
                view.created.join(", ")
              )}
            </Field>
            <Field label="Boot commit">
              {view.commit ?? (
                <span className="text-slate-500">null — the boot run changed nothing</span>
              )}
            </Field>
          </CardContent>
        </Card>
      )}

      {view.kind === "broken" && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Substrate</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Field label="Root">{view.root}</Field>
          </CardContent>
        </Card>
      )}

      {/* Rendered whatever the home state says: jobs failing while the home
          reads ready is a real and interesting combination, and hiding the
          panel behind `ready` would suppress it. */}
      <JobsPanel result={jobs} />
    </div>
  );
}
